import {createSelector} from 'reselect'

import type {ActionCreator} from '../actions/types'
import {openEditDialog} from '../components/common/EditProjectSidebar/EditProjectSidebar'
import type {State, TogglingOverview} from '../reducers/types'
import {federationServersArg} from '../rest/federationServers'
import {getFavoriteBuildsHref, getFavoriteProjectsHref} from '../routes'
import type {BuildType} from '../services/rest'
import {restApi} from '../services/rest'
import type {
  BuildTypeId,
  CustomSidebarItemId,
  ExpandState,
  FederationServerData,
  FederationServerId,
  NormalizedProject,
  ProjectId,
  Template,
  TitleId,
} from '../types'
import {
  ALL_PROJECTS_TITLE_ID,
  FAVORITES_TITLE_ID,
  ROOT_PROJECT_ID,
  stringifyId,
  toCustomSidebarItemId,
} from '../types'
import {base_uri} from '../types/BS_types'
import type {ProjectsTreeItem, SidebarActiveItem} from '../types/projectTrees'
import {currentServerId} from '../types/projectTrees'
import {convertDataToTree, upgradeTreeByExpandState} from '../utils/convertDataToTree'
import {emptyArray, emptyMap, getEmptyHash} from '../utils/empty'
import type {KeyValue, WritableMap} from '../utils/object'
import {objectEntries} from '../utils/object'

import {
  getNoMatchingWords,
  getBuildTypesHash,
  getExtensionEndpoint,
  getOverviewBuildTypesHash,
  getOverviewData,
  getOverviewProjectsHash,
  getProjectsHash,
  getProjectsState,
  getProjectTemplates,
  isArchivedProject,
  isArchivedSubprojectLoadedForAllProjects,
} from '.'

export type SidebarItemId = string
export type ProjectsTree = {
  data: Array<ProjectsTreeItem>
  _hash?: WritableMap<SidebarItemId, ProjectsTreeItem>
  hash?: WritableMap<SidebarItemId, ProjectsTreeItem>
}
export const generateSidebarItemId = (
  server: FederationServerId,
  type: 'project' | 'buildType' | 'template' | 'title' | 'server' | 'link' | 'action',
  id?: ProjectId | BuildTypeId | TitleId | CustomSidebarItemId | FederationServerId,
): SidebarItemId => `${stringifyId(server)}_${type}_${stringifyId(id)}`
export const getOverviewExpandState = (state: State): KeyValue<ProjectId, ExpandState> => {
  const {endpoint} = getExtensionEndpoint(state, 'expandState') ?? {}
  return (
    (endpoint != null && restApi.endpoints.getExpandState.select(endpoint)(state).data) ||
    getEmptyHash()
  )
}
export const getAllProjectsExpandState = (state: State): KeyValue<ProjectId, ExpandState> =>
  state.allProjectsExpandState

const getSearchProjectsExpandState: (
  arg0: State,
) => KeyValue<ProjectId, ExpandState> | null | undefined = state => state.searchProjectsExpandState

export const getHasFavoriteProjects: (arg0: State) => boolean | null | undefined = state =>
  getOverviewData(state).length > 0

export const getProjectsData: (
  state: State,
  rootProjectId?: ProjectId,
) => ReadonlyArray<ProjectId> = (state, rootProjectId = ROOT_PROJECT_ID) => {
  const dataWithArchived = state.projectsWithArchived[rootProjectId]?.data ?? emptyArray
  if (dataWithArchived.length > 0) {
    return dataWithArchived
  }
  const {data} = getProjectsState(state, rootProjectId)
  return data.length > 0 ? data : getProjectsState(state, ROOT_PROJECT_ID).data
}
export const getCanShowAllProjects: (arg0: State) => boolean = createSelector(
  getProjectsData,
  (state: State) => state.entities.buildTypes,
  (state: State) => state.buildTypesLimit,
  (projects, buildTypes, limit) => projects.length > 0 && Object.keys(buildTypes).length <= limit,
)
export const getProjectsAndOverviewReady: (state: State, rootProjectId?: ProjectId) => boolean = (
  state,
  rootProjectId,
) =>
  getOverviewData(state) != null &&
  (getProjectsState(state, rootProjectId).receiveMeta?.sidebarProjectsLoaded === true ||
    getProjectsState(state).receiveMeta?.sidebarProjectsLoaded === true)
const getFederationServersResult =
  restApi.endpoints.getAllFederationServers.select(federationServersArg)
const getFederationServers: (state: State) => ReadonlyArray<FederationServerId> = state =>
  getFederationServersResult(state).data ?? emptyArray
export const getFederationServersData: (
  arg0: State,
) => KeyValue<FederationServerId, FederationServerData> = state => state.federationServersData

const favoritesTitleItem: ProjectsTreeItem = {
  itemType: 'title',
  id: FAVORITES_TITLE_ID,
  name: 'FAVORITES',
  level: 0,
  group: 'favorites',
  serverId: currentServerId,
  editable: true,
  focusable: false,
}

const getLinkAsTreeItem: (arg0: {
  href: string
  name: string
  level?: number
  serverId?: FederationServerId
  icon?: string | null | undefined
}) => ProjectsTreeItem = ({href, name, level = 0, serverId = currentServerId, icon}) => ({
  itemType: 'link',
  id: toCustomSidebarItemId(href),
  href,
  icon,
  name,
  level,
  group: 'favorites',
  serverId,
  focusable: true,
})

const favoriteBuildsLinkItem: ProjectsTreeItem = getLinkAsTreeItem({
  name: 'Builds',
  href: getFavoriteBuildsHref(),
  icon: 'star-filled-14px',
})
const favoriteBuildsTextLinkItem: ProjectsTreeItem = getLinkAsTreeItem({
  name: 'Favorite Builds',
  href: getFavoriteBuildsHref(),
})
const favoriteProjectsLinkItem = getLinkAsTreeItem({
  name: 'Projects',
  href: getFavoriteProjectsHref(),
  icon: 'star-filled-14px',
})
const allProjectsLinkItem = getLinkAsTreeItem({
  name: 'All Projects',
  href: getFavoriteProjectsHref(),
})

export const actions: Record<string, ActionCreator> = {
  addToFavorites: () => openEditDialog(),
}

const addFavoritesActionItem: ProjectsTreeItem = {
  itemType: 'action',
  id: toCustomSidebarItemId('add_to_favorites_action'),
  action: 'addToFavorites',
  icon: 'star-filled-14px',
  name: 'Add to favorites...',
  level: 0,
  group: 'none',
  serverId: currentServerId,
}
export const createAllProjectsTitleItem = (
  collapsed: boolean,
  isEdit = false,
): ProjectsTreeItem => ({
  itemType: 'title',
  id: ALL_PROJECTS_TITLE_ID,
  name: isEdit ? 'EVERYTHING' : 'PROJECTS',
  level: 0,
  group: 'all',
  expanded: !collapsed,
  hasChildren: !isEdit,
  serverId: currentServerId,
  focusable: true,
})
const getAllProjectsTitleItem: (arg0: State) => ProjectsTreeItem = createSelector(
  (state: State) => state.sidebar.allProjectsCollapsed,
  collapsed => createAllProjectsTitleItem(collapsed),
)

const getServerTitleItem = (
  id: FederationServerId,
  authorized = true,
  loading = false,
): ProjectsTreeItem => ({
  id,
  loading,
  authorized,
  level: 0,
  hasChildren: true,
  itemType: 'server',
  name: stringifyId(id),
  expanded: true,
  serverId: id,
  group: 'search',
})

export type TreeFilter = {
  parentId?: ProjectId
  removeBuildTypes?: boolean
  removeEmptyProjects?: boolean
  includedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  excludedBuildTypes?: ReadonlyArray<BuildTypeId> | null | undefined
  includedProjects?: ReadonlyArray<ProjectId> | null | undefined
  includedRootProjects?: ReadonlyArray<ProjectId> | null | undefined
  disabledProjects?: ReadonlyArray<ProjectId> | null | undefined
  includeRoot?: boolean
  includeTemplates?: boolean
}

export const createDefaultOverviewTree = (
  ready: boolean,
  data: readonly ProjectId[],
  buildTypes: KeyValue<BuildTypeId, BuildType>,
  projects: KeyValue<ProjectId, NormalizedProject>,
  allProjects: KeyValue<ProjectId, NormalizedProject>,
  filter?: TreeFilter,
) =>
  convertDataToTree({
    ready,
    data,
    buildTypes,
    projects,
    group: 'favorites',
    allProjects,
    filter,
    onlyIncluded: true,
  })

export const getDefaultOverviewTree: (state: State) => ProjectsTree | null | undefined =
  createSelector(
    (state: State) => getProjectsAndOverviewReady(state),
    getOverviewData,
    getOverviewBuildTypesHash,
    getOverviewProjectsHash,
    getProjectsHash,
    () => undefined,
    createDefaultOverviewTree,
  )
const getOverviewTree: (arg0: State) => ProjectsTree = createSelector(
  getDefaultOverviewTree,
  getOverviewExpandState,
  (defaultTree, expandState) =>
    upgradeTreeByExpandState({
      defaultTree,
      expandState,
      baseLevel: 0,
    }),
)
const getOverviewTreeData: (arg0: State) => ReadonlyArray<ProjectsTreeItem> = createSelector(
  getOverviewTree,
  tree => tree.data,
)
export const getOverviewTreeHash: (state: State) => WritableMap<SidebarItemId, ProjectsTreeItem> =
  createSelector(getOverviewTree, tree => tree?.hash || emptyMap)

export const getAllProjectsLoaded = (state: State) =>
  getProjectsState(state).receiveMeta?.sidebarProjectsLoaded || false
const prepareAllProjectAndBuildTypesToConvert: (arg0: State) => {
  ready: boolean
  data: any
  buildTypes: any
  projects: any
} = createSelector(
  getAllProjectsLoaded,
  getProjectsData,
  getBuildTypesHash,
  getProjectsHash,
  (state: State) => state.sidebar.showArchivedProjects,
  (ready, data, buildTypes, projects, showArchivedProjects) => ({
    ready,
    data: showArchivedProjects ? data : data.filter(id => !projects[id]?.archived),
    buildTypes,
    projects,
  }),
)
export const getDefaultAllProjectsTree: (arg0: State) => ProjectsTree | null | undefined =
  createSelector(
    prepareAllProjectAndBuildTypesToConvert,
    (state: State) => state.sidebar.showArchivedProjects,
    ({ready, data, buildTypes, projects}, showArchivedProjects) =>
      convertDataToTree({
        ready,
        data,
        buildTypes,
        projects,
        group: 'all',
        showArchivedProjects,
      }),
  )

export const getProjectSubTree: (
  arg0: State,
  parentId: ProjectId,
) => ProjectsTree | null | undefined = createSelector(
  (state: State, parentId: ProjectId) => parentId,
  (state: State) => prepareAllProjectAndBuildTypesToConvert(state),
  (parentId, {ready, data, buildTypes, projects}) =>
    convertDataToTree({
      ready,
      data,
      buildTypes,
      projects,
      group: 'all',
      filter: {parentId},
    }),
)

const getAllProjectsTree: (state: State) => ProjectsTree = createSelector(
  getDefaultAllProjectsTree,
  getAllProjectsExpandState,
  (state: State) => state.sidebar.showArchivedProjects,
  (defaultTree, expandState, showArchivedProjects) =>
    upgradeTreeByExpandState({
      defaultTree,
      expandState,
      baseLevel: 1,
      showArchivedProjects,
    }),
)
const getAllProjectsTreeData: (arg0: State) => ReadonlyArray<ProjectsTreeItem> | null | undefined =
  createSelector(getAllProjectsTree, tree => tree?.data)
export const getAllProjectsTreeHash: (
  state: State,
) => WritableMap<SidebarItemId, ProjectsTreeItem> = createSelector(
  getAllProjectsTree,
  tree => tree?.hash || emptyMap,
)
const getMergedProjectsData: (arg0: State, arg1?: ProjectId) => ReadonlyArray<ProjectId> =
  createSelector(getProjectsData, getOverviewData, (data, overviewData) => [
    ...new Set([...overviewData, ...data]),
  ])
const makeGetDefaultSearchProjectsWithArchivedTree = (filter: TreeFilter = Object.freeze({})) =>
  createSelector(
    (state: State) => getProjectsAndOverviewReady(state, filter.parentId),
    (state: State) => getMergedProjectsData(state, filter.parentId),
    getBuildTypesHash,
    getProjectsHash,
    (state: State): KeyValue<ProjectId, readonly Template[]> =>
      filter.includeTemplates ? getProjectTemplates(state) : getEmptyHash(),
    getFederationServers,
    getFederationServersData,
    (ready, data, buildTypes, projects, templates, federationServers, federationServersData) => {
      const result: WritableMap<FederationServerId, ProjectsTree | null | undefined> = new Map()
      const currentServerTree = convertDataToTree({
        ready,
        data,
        buildTypes,
        projects,
        templates,
        group: 'search',
        filter,
        showArchivedProjects: true,
      })

      if (currentServerTree) {
        result.set(currentServerId, currentServerTree)
      }

      federationServers.forEach(serverId => {
        const serverData = federationServersData[serverId]
        const serverEntities = serverData?.projects?.entities

        if (serverData?.projects && serverEntities?.buildTypes && serverEntities.projects) {
          const serverTree = convertDataToTree({
            ready,
            data: serverData.projects.result,
            buildTypes: serverEntities.buildTypes,
            projects: serverEntities.projects,
            group: 'search',
            serverId,
            showArchivedProjects: true,
          })

          if (serverTree) {
            result.set(serverId, serverTree)
          }
        }
      })

      return result
    },
  )

function getSearchProjectsWithoutArchivedTree(
  treeByServer: WritableMap<FederationServerId, ProjectsTree | null | undefined>,
  federationServers: ReadonlyArray<FederationServerId>,
) {
  const servers = [currentServerId, ...federationServers]
  const result: WritableMap<FederationServerId, ProjectsTree> = new Map()

  servers.forEach((serverId: FederationServerId) => {
    const tree = treeByServer.get(serverId)

    if (tree?.data) {
      const data: ProjectsTreeItem[] = []

      tree.data.forEach(item => {
        const {archived} = item

        if (!archived) {
          data.push(item)
        }
      })

      result.set(serverId, {
        data,
      })
    }
  })
  return result
}
export const makeGetSearchProjectsWithoutArchivedTree = (filter?: TreeFilter) =>
  createSelector(
    makeGetDefaultSearchProjectsWithArchivedTree(filter),
    getFederationServers,
    getSearchProjectsWithoutArchivedTree,
  )
export const makeGetDefaultSearchProjectsTree = (filter?: TreeFilter) =>
  createSelector(
    (state: State) => state.sidebar.showArchivedProjects,
    isArchivedSubprojectLoadedForAllProjects,
    makeGetDefaultSearchProjectsWithArchivedTree(filter),
    (state: State) => isArchivedProject(state, filter?.parentId),
    getFederationServers,
    (
      showArchivedProjects,
      archivedReady,
      defaultTreeWithArchived,
      isParentArchived,
      treeByServer,
    ) => {
      if ((showArchivedProjects || isParentArchived) && archivedReady) {
        return defaultTreeWithArchived
      }

      return getSearchProjectsWithoutArchivedTree(defaultTreeWithArchived, treeByServer)
    },
  )
const getDefaultSearchProjectsTree = makeGetDefaultSearchProjectsTree()
export const makeHasArchivedDescendants = (filter: TreeFilter | undefined) => {
  const selector = makeGetDefaultSearchProjectsWithArchivedTree(filter)
  return (state: State) =>
    selector(state)
      .get(currentServerId)
      ?.data.some(item => item.archived) ?? false
}
export function getIsStarred({
  item,
  togglingOverview,
  overviewData,
  overviewProjectsHash,
}: {
  item: ProjectsTreeItem
  togglingOverview: TogglingOverview
  overviewData: Set<ProjectId>
  overviewProjectsHash: KeyValue<ProjectId, NormalizedProject>
}): boolean | null | undefined {
  if (item.isStarred != null) {
    return item.isStarred
  }

  if (item.serverId !== currentServerId) {
    return null
  }

  if (item.itemType === 'project') {
    const toggling = togglingOverview.project[item.id]
    return toggling ?? overviewData.has(item.id)
  } else if (item.itemType === 'buildType') {
    const toggling = togglingOverview.bt[item.id!]

    if (toggling != null) {
      return toggling
    } else if (overviewData.has(item.parentId)) {
      const buildTypes = overviewProjectsHash[item.parentId]?.buildTypes?.buildType
      return buildTypes ? buildTypes.includes(item.id!) : false
    } else {
      return false
    }
  }

  return null
}
type CreateSearchProjectsTreeParams = {
  searchQuery: string
  defaultTreeByServer: WritableMap<FederationServerId, ProjectsTree | null | undefined>
  togglingOverview: TogglingOverview
  overviewData: ReadonlyArray<ProjectId>
  overviewProjectsHash: KeyValue<ProjectId, NormalizedProject>
  federationServers?: ReadonlyArray<FederationServerId>
  parentId?: ProjectId
  includeRoot?: boolean
}
export function createSearchProjectsTree({
  searchQuery,
  defaultTreeByServer,
  togglingOverview,
  overviewData,
  overviewProjectsHash,
  federationServers = emptyArray,
  parentId = ROOT_PROJECT_ID,
  includeRoot = false,
}: CreateSearchProjectsTreeParams) {
  const servers = [currentServerId, ...federationServers]
  const result: WritableMap<FederationServerId, ProjectsTree> = new Map()
  const overviewDataSet = new Set(overviewData)
  const words: Array<string> = searchQuery.toLowerCase().split(' ').filter(Boolean)

  servers.forEach((serverId: FederationServerId) => {
    const defaultTree = defaultTreeByServer.get(serverId)
    let data: ProjectsTreeItem[] = []

    if (defaultTree?.data?.length) {
      const defaultTreeData = defaultTree.data
      const defaultTreeLength = defaultTreeData.length
      let index = 0

      if (words.length) {
        const worldStateMap = new Map()

        while (index < defaultTreeLength) {
          const treeItem = defaultTreeData[index]
          const {
            id: treeItemId,
            name: treeItemName,
            level: treeItemLevel,
            parentId: treeItemParentId,
            itemType: treeItemType,
          } = treeItem
          const possibleRootId = includeRoot ? treeItemId : treeItemParentId

          if (treeItemType === 'server') {
            data.push(treeItem)

            index++

            continue
          }

          if (possibleRootId == null || (possibleRootId !== parentId && treeItemParentId == null)) {
            index++
            continue
          }

          const nextIndex = index + 1
          const isItemTypeProject = treeItemType === 'project'
          const {level: nextTreeItemLevel} = defaultTreeData[nextIndex] ?? {}
          const lastParentMatchWorlds = worldStateMap.get(treeItemParentId) || words
          const remainingWorlds = getNoMatchingWords(
            treeItemName.toLowerCase(),
            lastParentMatchWorlds,
          )
          const isFullMatch = !remainingWorlds.length

          treeItem.isFullMatch = isFullMatch

          if (isItemTypeProject && isFullMatch) {
            do {
              const item = defaultTreeData[index]
              item.isFullMatch = true
              item.isStarred = getIsStarred({
                item,
                togglingOverview,
                overviewData: overviewDataSet,
                overviewProjectsHash,
              })
              data.push(item)

              index++
            } while (defaultTreeData[index]?.level > treeItemLevel)

            continue
          }

          data.push(treeItem)

          if (isItemTypeProject) {
            worldStateMap.set(treeItemId, remainingWorlds)
          }

          if (!isFullMatch && treeItemLevel >= nextTreeItemLevel) {
            let reverseIndex = data.length - 1

            while (
              data[reverseIndex] &&
              !data[reverseIndex]?.isFullMatch &&
              data[reverseIndex]?.level >= nextTreeItemLevel
            ) {
              data.pop()
              reverseIndex--
            }
          } else if (nextTreeItemLevel <= treeItemLevel || !nextTreeItemLevel) {
            let reverseIndex = data.length - 1

            while (data[reverseIndex] && !data[reverseIndex]?.isFullMatch) {
              data.pop()
              reverseIndex--
            }
          } else {
            treeItem.isStarred = getIsStarred({
              item: treeItem,
              togglingOverview,
              overviewData: overviewDataSet,
              overviewProjectsHash,
            })
          }

          index++
        }
      } else {
        data = [...defaultTree?.data]
      }
    }

    result.set(serverId, {
      data,
    })
  })

  return result
}

const getSearchProjectsTreeDefault: (arg0: State) => WritableMap<FederationServerId, ProjectsTree> =
  createSelector(
    (state: State) => state.sidebar.searchQuery,
    getDefaultSearchProjectsTree,
    (state: State) => state.togglingOverview,
    getOverviewData,
    getOverviewProjectsHash,
    getFederationServers,
    (
      searchQuery,
      defaultTreeByServer,
      togglingOverview,
      overviewData,
      overviewProjectsHash,
      federationServers,
    ) =>
      createSearchProjectsTree({
        searchQuery,
        defaultTreeByServer,
        togglingOverview,
        overviewData,
        overviewProjectsHash,
        federationServers,
      }),
  )

export const upgradeSearchProjectsTreeByExpandState = (
  federationServers: ReadonlyArray<FederationServerId>,
  searchProjectsTree: WritableMap<FederationServerId, ProjectsTree>,
  expandStates: KeyValue<ProjectId, ExpandState> | null | undefined,
  defaultExpandState = true,
) => {
  const expandStatesEntries = expandStates != null ? objectEntries(expandStates) : []
  const expandStateMap = new Map(expandStatesEntries)
  const expandStateMapSize = expandStateMap.size
  const servers = [currentServerId, ...federationServers]

  if (expandStateMapSize || !defaultExpandState) {
    const result: WritableMap<FederationServerId, ProjectsTree> = new Map()

    servers.forEach(server => {
      const projectsTree = searchProjectsTree.get(server)
      const data = []
      let index = 0
      let expandParentLevel = Infinity

      if (projectsTree?.data?.length) {
        while (index < projectsTree?.data.length) {
          const treeItem = projectsTree.data[index]
          const {itemType, level, id} = treeItem

          index++

          if (level > expandParentLevel) {
            continue
          } else {
            expandParentLevel = Infinity
          }

          data.push(treeItem)

          if (treeItem && itemType === 'project') {
            const expandState = expandStateMap.get(id)?.expandState
            const isExpanded = expandState ? expandState === 'EXPANDED' : defaultExpandState

            treeItem.expanded = isExpanded

            if (!isExpanded && expandParentLevel === Infinity) {
              expandParentLevel = level
            }
          }
        }
      }

      result.set(server, {
        data,
        _hash: undefined,
        get hash() {
          if (!this._hash) {
            this._hash = new Map(this.data.map(item => [item.key!, item]))
          }

          return this._hash
        },
      })
    })

    return result
  }

  return searchProjectsTree
}

const getSearchProjectsTree: (arg0: State) => WritableMap<FederationServerId, ProjectsTree> =
  createSelector(
    getFederationServers,
    getSearchProjectsTreeDefault,
    getSearchProjectsExpandState,
    upgradeSearchProjectsTreeByExpandState,
  )

export const mergeSearchProjectsTree = (
  treeByServer: WritableMap<FederationServerId, ProjectsTree>,
  federationServers: ReadonlyArray<FederationServerId>,
  federationServersData: KeyValue<FederationServerId, FederationServerData>,
): ProjectsTree => {
  let treeData: Array<ProjectsTreeItem> = []
  const servers = [currentServerId, ...federationServers]

  servers.forEach((serverId: FederationServerId) => {
    const tree = treeByServer.get(serverId)
    const serverData = federationServersData[serverId]

    if (tree?.data && (tree.data.length || (serverId !== base_uri && !serverData?.authorized))) {
      if (federationServers?.length) {
        if (serverId === base_uri) {
          treeData.push(getServerTitleItem(currentServerId))
        } else {
          const serverTitle = getServerTitleItem(
            serverId,
            Boolean(serverData?.authorized),
            serverData?.projects == null,
          )
          treeData.push(serverTitle)
        }
      }

      if (serverId === base_uri || Boolean(serverData?.authorized)) {
        treeData = treeData.concat(tree.data)
      }
    }
  })

  return {
    data: treeData,
  }
}
const getSearchProjectsTreeMerged: (state: State) => ProjectsTree = createSelector(
  getSearchProjectsTree,
  getFederationServers,
  getFederationServersData,
  mergeSearchProjectsTree,
)
const getSearchProjectsTreeData: (arg0: State) => ReadonlyArray<ProjectsTreeItem> = createSelector(
  getSearchProjectsTreeMerged,
  tree => tree?.data || emptyArray,
)
export const getSearchProjectsTreeHash: (
  state: State,
) => WritableMap<SidebarItemId, ProjectsTreeItem> = createSelector(
  getSearchProjectsTreeMerged,
  tree => tree?.hash || emptyMap,
)
export function mergeProjectsForSidebar(
  favoriteProjects: ReadonlyArray<ProjectsTreeItem>,
  hasFavoriteProjects: boolean | null | undefined,
  allProjects: ReadonlyArray<ProjectsTreeItem> | null | undefined,
  allProjectsTitleItem: ProjectsTreeItem,
  userCanEdit: boolean,
  canShowAllProjects: boolean,
  isEdit: boolean,
): ReadonlyArray<ProjectsTreeItem> {
  let items: Array<ProjectsTreeItem> = []

  if (hasFavoriteProjects === true) {
    items.push(favoritesTitleItem)

    if (!isEdit) {
      items.push(favoriteProjectsLinkItem)
      items.push(favoriteBuildsLinkItem)
    }

    items = items.concat(favoriteProjects)
  } else if (hasFavoriteProjects === false) {
    if (!isEdit && userCanEdit && allProjects != null && allProjects.length > 0) {
      items.push(addFavoritesActionItem)
    }

    if (!isEdit && canShowAllProjects) {
      items.push(allProjectsLinkItem)
    }

    if (!isEdit) {
      items.push(favoriteBuildsTextLinkItem)
    }
  }

  if (allProjects?.length) {
    items.push(allProjectsTitleItem)

    if (allProjectsTitleItem.expanded === true) {
      items = items.concat(allProjects)
    }
  }

  return items.length ? items : emptyArray
}
const getProjectsForSidebar: (
  state: State,
  canChangeOwnProfile: boolean,
) => ReadonlyArray<ProjectsTreeItem> = createSelector(
  getOverviewTreeData,
  getHasFavoriteProjects,
  getAllProjectsTreeData,
  getAllProjectsTitleItem,
  (_: State, canChangeOwnProfile: boolean) => canChangeOwnProfile,
  getCanShowAllProjects,
  () => false,
  mergeProjectsForSidebar,
)
export const getProjectsForSidebarOrSearch = (
  state: State,
  canChangeOwnProfile: boolean,
): ReadonlyArray<ProjectsTreeItem> =>
  state.sidebar.searchActive && state.sidebar.searchQuery
    ? getSearchProjectsTreeData(state)
    : getProjectsForSidebar(state, canChangeOwnProfile)

export const matchSidebarItem =
  (activeItem: SidebarActiveItem | null | undefined) =>
  ({group: nodeGroup, itemType, id: nodeId}: ProjectsTreeItem): boolean => {
    if (activeItem == null) {
      return false
    }
    const {nodeType, id: activeItemId, group: activeItemGroup} = activeItem

    if (nodeType === 'bt' || nodeType === 'project') {
      if (nodeGroup !== activeItemGroup && nodeGroup !== 'all' && nodeGroup !== 'search') {
        return false
      }
    }

    switch (itemType) {
      case 'project':
        return nodeType === 'project' && activeItemId === nodeId

      case 'buildType':
        return nodeType === 'bt' && activeItemId === nodeId

      case 'template':
        return nodeType === 'template' && activeItemId === nodeId

      case 'link':
        return nodeType === 'link' && activeItemId === nodeId

      case 'action':
        return nodeType === 'all' && nodeId === toCustomSidebarItemId('all item')

      default:
        return false
    }
  }
